/** Cost Center (D-179 แยกหน้า) — ดู/กรอง/สร้าง/แก้/ปิดใช้งาน พร้อมประเภท ผู้รับผิดชอบ และแผนก; ไม่มีปุ่มลบ */

import { useEffect, useState, type FormEvent } from 'react';

import { RecordTable, type RecordColumn } from '../components/RecordTable';
import { can } from '../../lib/permissions/client';
import type { PermissionsState } from '../../lib/permissions/types';
import { fetchRecords, patchRecord, postRecord, type RecordRow } from '../../lib/backend/records';
import { callJson, stockFailureText, type StockFailure } from '../../lib/inventory/stock';
import { fetchDepartments, fetchEmployees } from '../../lib/rbacAdmin/client';

const MODULE = 'MASTER_DATA';

const COST_CENTER_TYPE_TEXT: Readonly<Record<string, string>> = {
  PRODUCTION: 'ผลิต',
  SUPPORT: 'สนับสนุน',
  ADMIN: 'บริหาร',
};
const STATUS_TEXT: Readonly<Record<string, string>> = { ACTIVE: 'ใช้งาน', INACTIVE: 'ปิดใช้งาน' };

const RULE_TEXT: Readonly<Record<string, string>> = {
  RESPONSIBLE_EMPLOYEE_NOT_FOUND: 'ไม่พบพนักงานผู้รับผิดชอบคนนี้',
  RESPONSIBLE_EMPLOYEE_NOT_ACTIVE: 'พนักงานผู้รับผิดชอบคนนี้ไม่ได้ใช้งานอยู่',
  DEPARTMENT_NOT_FOUND: 'ไม่พบแผนกนี้',
  COST_CENTER_INACTIVE: 'Cost Center นี้ถูกปิดใช้งานแล้ว',
};

function failureText(result: StockFailure): string {
  if (result.status === 'rejected' && result.rule !== null) {
    if (result.rule === 'COST_CENTER_IN_USE') {
      const raw = result.details?.work_center_codes;
      const codes = Array.isArray(raw) ? raw.map(String).join(', ') : '';
      return `ปิดใช้งานไม่ได้: ยังมี Work Center ใช้ Cost Center นี้อยู่${codes === '' ? '' : ` (${codes})`} — ย้ายหรือปิด Work Center เหล่านั้นก่อน`;
    }
    const known = RULE_TEXT[result.rule];
    if (known !== undefined) {
      return known;
    }
  }
  return stockFailureText(result);
}

const str = (v: unknown): string => (typeof v === 'string' ? v : '');

interface IoOptions {
  readonly apiBaseUrl?: string;
  readonly fetchImpl?: typeof fetch;
}

interface Option {
  readonly id: string;
  readonly label: string;
}

/** รายชื่อพนักงาน/แผนกจาก RBAC admin client เดิม; โหลดไม่ได้ (เช่นไม่มีสิทธิ์ ADMIN) = null → พิมพ์ id เอง */
function useOptions(io: IoOptions): { employees: readonly Option[] | null; departments: readonly Option[] | null } {
  const [state, setState] = useState<{ employees: readonly Option[] | null; departments: readonly Option[] | null }>({
    employees: null,
    departments: null,
  });
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const [emp, dep] = await Promise.all([fetchEmployees(io), fetchDepartments(io)]);
      if (cancelled) {
        return;
      }
      setState({
        employees:
          emp.status === 'ready'
            ? emp.page.items
                .filter((e) => e.status === 'ACTIVE')
                .map((e) => ({ id: e.employee_id, label: `${e.display_name} (${e.employee_id})` }))
            : null,
        departments:
          dep.status === 'ready'
            ? dep.page.items.map((d) => ({ id: d.department_id, label: d.department_name }))
            : null,
      });
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [io.apiBaseUrl, io.fetchImpl]);
  return state;
}

function Picker({
  label,
  options,
  value,
  onChange,
}: {
  readonly label: string;
  readonly options: readonly Option[] | null;
  readonly value: string;
  readonly onChange: (v: string) => void;
}) {
  return (
    <label className="cap-field">
      <span className="cap-field__label">{options === null ? `${label} (ระบุรหัส)` : label}</span>
      {options === null ? (
        <input className="cap-input" aria-label={label} value={value} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <select className="cap-input" aria-label={label} value={value} onChange={(e) => onChange(e.target.value)}>
          <option value="">— ไม่ระบุ —</option>
          {value !== '' && !options.some((o) => o.id === value) && <option value={value}>{value}</option>}
          {options.map((o) => (
            <option key={o.id} value={o.id}>
              {o.label}
            </option>
          ))}
        </select>
      )}
    </label>
  );
}

function TypeSelect({
  label,
  value,
  onChange,
}: {
  readonly label: string;
  readonly value: string;
  readonly onChange: (v: string) => void;
}) {
  return (
    <label className="cap-field">
      <span className="cap-field__label">{label}</span>
      <select className="cap-input" aria-label={label} value={value} onChange={(e) => onChange(e.target.value)}>
        {Object.entries(COST_CENTER_TYPE_TEXT).map(([k, v]) => (
          <option key={k} value={k}>
            {v}
          </option>
        ))}
      </select>
    </label>
  );
}

const COLUMNS: readonly RecordColumn[] = [
  { key: 'plant_code', label: 'โรงงาน' },
  { key: 'cost_center_code', label: 'รหัส' },
  { key: 'name', label: 'ชื่อ' },
  { key: 'type_text', label: 'ประเภท' },
  { key: 'status_text', label: 'สถานะ' },
  { key: 'responsible_text', label: 'ผู้รับผิดชอบ' },
  { key: 'department_text', label: 'แผนก' },
  { key: 'work_center_count', label: 'จำนวน Work Center' },
];

function display(row: RecordRow): RecordRow {
  return {
    ...row,
    type_text: COST_CENTER_TYPE_TEXT[str(row.cost_center_type)] ?? row.cost_center_type,
    status_text: STATUS_TEXT[str(row.status)] ?? row.status,
    responsible_text: row.responsible_employee_name ?? row.responsible_employee_id,
    department_text: row.department_name ?? row.department_id,
  };
}

export interface CostCenterScreenProps {
  readonly permissions: PermissionsState;
  readonly apiBaseUrl?: string;
  readonly fetchImpl?: typeof fetch;
}

interface Editing {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly type: string;
  readonly responsible: string;
  readonly department: string;
  readonly workCenters: readonly string[];
}

const BLANK = {
  plant_code: '',
  cost_center_code: '',
  name: '',
  cost_center_type: 'PRODUCTION',
  responsible: '',
  department: '',
};

export function CostCenterScreen({ permissions, apiBaseUrl, fetchImpl }: CostCenterScreenProps) {
  const canCreate = can(permissions, MODULE, 'CREATE');
  const canEdit = can(permissions, MODULE, 'EDIT');
  const io: IoOptions = {
    ...(apiBaseUrl === undefined ? {} : { apiBaseUrl }),
    ...(fetchImpl === undefined ? {} : { fetchImpl }),
  };
  const options = useOptions(io);
  const [filters, setFilters] = useState({ status: '', cost_center_type: '', plant_code: '' });
  const [rows, setRows] = useState<readonly RecordRow[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [form, setForm] = useState(BLANK);
  const [editing, setEditing] = useState<Editing | null>(null);

  const filterKey = JSON.stringify(filters);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const result = await fetchRecords(
        '/v1/cost-centers',
        { ...filters, plant_code: filters.plant_code.toUpperCase() },
        io,
      );
      if (cancelled) {
        return;
      }
      if (result.status !== 'ready') {
        setRows([]);
        setLoadError(stockFailureText(result));
        return;
      }
      setLoadError(null);
      setRows(result.items);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey, reload, apiBaseUrl, fetchImpl]);

  const create = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setNotice(null);
    if ([form.plant_code, form.cost_center_code, form.name].some((v) => v.trim() === '')) {
      setError('กรอกโรงงาน รหัส และชื่อให้ครบ');
      return;
    }
    const result = await postRecord(
      '/v1/cost-centers',
      {
        plant_code: form.plant_code.trim().toUpperCase(),
        cost_center_code: form.cost_center_code.trim().toUpperCase(),
        name: form.name.trim(),
        cost_center_type: form.cost_center_type,
        ...(form.responsible.trim() === '' ? {} : { responsible_employee_id: form.responsible.trim() }),
        ...(form.department.trim() === '' ? {} : { department_id: form.department.trim() }),
      },
      {},
      io,
    );
    if (result.status !== 'ready') {
      setError(failureText(result));
      return;
    }
    setNotice('บันทึกแล้ว');
    setForm(BLANK);
    setReload((n) => n + 1);
  };

  const patch = async (id: string, body: Record<string, unknown>, done: string): Promise<boolean> => {
    setError(null);
    setNotice(null);
    const result = await patchRecord(`/v1/cost-centers/${encodeURIComponent(id)}`, body, {}, io);
    if (result.status !== 'ready') {
      setError(failureText(result));
      return false;
    }
    setNotice(done);
    setReload((n) => n + 1);
    return true;
  };

  const openEdit = async (row: RecordRow) => {
    const id = str(row.cost_center_id);
    setError(null);
    setNotice(null);
    setEditing({
      id,
      code: str(row.cost_center_code),
      name: str(row.name),
      type: str(row.cost_center_type) || 'PRODUCTION',
      responsible: str(row.responsible_employee_id),
      department: str(row.department_id),
      workCenters: [],
    });
    const detail = await callJson('GET', `/v1/cost-centers/${encodeURIComponent(id)}`, undefined, {}, io);
    if (detail.status === 'ok' && typeof detail.payload === 'object' && detail.payload !== null) {
      const list = (detail.payload as RecordRow).work_centers;
      if (Array.isArray(list)) {
        const codes = list.map((w) => str((w as RecordRow).work_center_code)).filter((c) => c !== '');
        setEditing((e) => (e !== null && e.id === id ? { ...e, workCenters: codes } : e));
      }
    }
  };

  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (editing === null) {
      return;
    }
    if (editing.name.trim() === '') {
      setError('กรอกชื่อ');
      return;
    }
    const ok = await patch(
      editing.id,
      {
        name: editing.name.trim(),
        cost_center_type: editing.type,
        responsible_employee_id: editing.responsible.trim() === '' ? null : editing.responsible.trim(),
        department_id: editing.department.trim() === '' ? null : editing.department.trim(),
      },
      'บันทึกการแก้ไขแล้ว',
    );
    if (ok) {
      setEditing(null);
    }
  };

  const rowActions = (row: RecordRow) => {
    const active = row.status === 'ACTIVE';
    return (
      <>
        <button type="button" className="cap-button" onClick={() => void openEdit(row)}>
          แก้ไข
        </button>{' '}
        <button
          type="button"
          className="cap-button"
          onClick={() =>
            void patch(
              str(row.cost_center_id),
              { status: active ? 'INACTIVE' : 'ACTIVE' },
              active ? 'ปิดใช้งานแล้ว' : 'เปิดใช้งานแล้ว',
            )
          }
        >
          {active ? 'ปิดใช้งาน' : 'เปิดใช้งาน'}
        </button>
      </>
    );
  };

  return (
    <div className="cap-page" data-testid="cost-centers">
      <div className="cap-page__head">
        <h1 className="cap-page__title">Cost Center</h1>
      </div>
      {error !== null && (
        <p className="cap-notice__note" role="alert" data-testid="cost-center-error">
          {error}
        </p>
      )}
      {notice !== null && (
        <p className="cap-notice__text" role="status" data-testid="cost-center-notice">
          {notice}
        </p>
      )}
      {loadError !== null && (
        <p className="cap-notice__note" role="alert" data-testid="cost-center-table-error">
          {loadError}
        </p>
      )}

      <div className="cap-filters">
        <label className="cap-field">
          <span className="cap-field__label">สถานะ</span>
          <select
            className="cap-input"
            aria-label="กรองสถานะ"
            value={filters.status}
            onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
          >
            <option value="">ทั้งหมด</option>
            <option value="ACTIVE">ใช้งาน</option>
            <option value="INACTIVE">ปิดใช้งาน</option>
          </select>
        </label>
        <label className="cap-field">
          <span className="cap-field__label">ประเภท</span>
          <select
            className="cap-input"
            aria-label="กรองประเภท"
            value={filters.cost_center_type}
            onChange={(e) => setFilters((f) => ({ ...f, cost_center_type: e.target.value }))}
          >
            <option value="">ทั้งหมด</option>
            {Object.entries(COST_CENTER_TYPE_TEXT).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </label>
        <label className="cap-field">
          <span className="cap-field__label">โรงงาน</span>
          <input
            className="cap-input"
            aria-label="กรองโรงงาน"
            value={filters.plant_code}
            onChange={(e) => setFilters((f) => ({ ...f, plant_code: e.target.value }))}
          />
        </label>
      </div>

      <RecordTable
        rows={rows.map(display)}
        columns={COLUMNS}
        testId="cost-center-table"
        {...(canEdit ? { rowActions } : {})}
      />

      {canEdit && editing !== null && (
        <form
          className="cap-card cap-card--form cap-form"
          data-testid="cost-center-edit-form"
          onSubmit={(e) => void save(e)}
        >
          <h2 className="cap-notice__title">แก้ไข Cost Center {editing.code}</h2>
          <div className="cap-form__grid">
            <label className="cap-field">
              <span className="cap-field__label">ชื่อ</span>
              <input
                className="cap-input"
                aria-label="แก้ไข: ชื่อ"
                value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
              />
            </label>
            <TypeSelect
              label="แก้ไข: ประเภท"
              value={editing.type}
              onChange={(v) => setEditing({ ...editing, type: v })}
            />
            <Picker
              label="แก้ไข: ผู้รับผิดชอบ"
              options={options.employees}
              value={editing.responsible}
              onChange={(v) => setEditing({ ...editing, responsible: v })}
            />
            <Picker
              label="แก้ไข: แผนก"
              options={options.departments}
              value={editing.department}
              onChange={(v) => setEditing({ ...editing, department: v })}
            />
          </div>
          {editing.workCenters.length > 0 && (
            <p className="cap-notice__text" data-testid="cost-center-work-centers">
              Work Center ที่ใช้: {editing.workCenters.join(', ')}
            </p>
          )}
          <div className="cap-form__actions cap-form__actions--padded">
            <button type="submit" className="cap-button cap-button--primary">
              บันทึกการแก้ไข
            </button>
            <button type="button" className="cap-button" onClick={() => setEditing(null)}>
              ยกเลิก
            </button>
          </div>
        </form>
      )}

      {canCreate && (
        <form
          className="cap-card cap-card--form cap-form"
          data-testid="cost-center-form"
          onSubmit={(e) => void create(e)}
        >
          <div className="cap-form__grid">
            {(
              [
                ['Cost Center: โรงงาน', 'plant_code'],
                ['Cost Center: รหัส', 'cost_center_code'],
                ['Cost Center: ชื่อ', 'name'],
              ] as const
            ).map(([label, key]) => (
              <label className="cap-field" key={key}>
                <span className="cap-field__label">{label}</span>
                <input
                  className="cap-input"
                  aria-label={label}
                  value={form[key]}
                  onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                />
              </label>
            ))}
            <TypeSelect
              label="Cost Center: ประเภท"
              value={form.cost_center_type}
              onChange={(v) => setForm((f) => ({ ...f, cost_center_type: v }))}
            />
            <Picker
              label="Cost Center: ผู้รับผิดชอบ"
              options={options.employees}
              value={form.responsible}
              onChange={(v) => setForm((f) => ({ ...f, responsible: v }))}
            />
            <Picker
              label="Cost Center: แผนก"
              options={options.departments}
              value={form.department}
              onChange={(v) => setForm((f) => ({ ...f, department: v }))}
            />
          </div>
          <div className="cap-form__actions cap-form__actions--padded">
            <button type="submit" className="cap-button cap-button--primary">
              เพิ่ม Cost Center
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
